import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useQuery } from '@tanstack/react-query'
import useApi from '../../hooks/useApi';
import safeDecode from '../../utils/safeDecode';

const ImageContainer = styled.div`
  width: ${props => props.$width || '4rem'};
  height: ${props => props.$height || '2rem'};
  padding: ${props =>
    props.theme.mode === 'dark'
      ? '0.375rem'
      : props.$padding || '0rem'};
  margin: ${props => props.$margin || '0'};
  background-color: ${props =>
    props.$type === 'solid'
      ? (props.$background || 'white')
      : props.theme.mode === 'dark'
        ? 'transparent'
        : 'transparent'};
  border-radius: ${props =>
    props.$type === 'solid' ? '0.375rem'
      : props.theme.mode === 'dark'
        ? '0.375rem'
        : '0'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const StyledImage = styled.img`
  filter: ${({ theme }) => theme.mode === 'dark' ? 'invert(1) grayscale(0.9)' : 'none'};
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const SkeletonDiv = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: ${props => props.$type === 'solid' ? '0.25rem' : '0'};

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const EmptyDiv = styled.div`
  width: 100%;
  height: 100%;
  background-color: transparent;
`;

const Image = ({
  path,
  width = '4rem',
  height = '2rem',
  type = 'transparent',
  background,
  padding = '0rem',
  margin = '0'
}) => {
  const { getMeStream } = useApi();

  let imagePath = safeDecode(path);

  // Fetch image using React Query
  const {
    data: imageBlob,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['image', imagePath], // Query key
    queryFn: async () => {
      if (!imagePath) {
        throw new Error('No path provided');
      }

      const response = await getMeStream(`image/${imagePath}`);

      // Check if response is an error
      if (response?.error || response?.status >= 400) {
        throw new Error(response?.error || 'Failed to fetch image');
      }

      return response;
    },
    enabled: !!imagePath, // Only run query if path exists
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 1, // Only retry once on failure
    refetchOnWindowFocus: false, // Don't refetch on window focus
    onError: (err) => {
      console.error('Error fetching image:', err);
    }
  });

  // Create object URL from blob when data is available
  const imageUrl = imageBlob ? URL.createObjectURL(imageBlob) : null;

  // Cleanup object URL when component unmounts or imageBlob changes
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  return (
    <ImageContainer
      $width={width}
      $height={height}
      $padding={padding}
      $type={type}
      $background={background}
      $margin={margin}
    >
      {isLoading && (
        <SkeletonDiv $type={type} />
      )}
      {!isLoading && !isError && imageUrl && (
        <StyledImage
          src={imageUrl}
          alt="Retrieved from storage"
          onError={() => {
            console.error('Image failed to load in browser');
          }}
        />
      )}
      {!isLoading && (isError || !imageUrl) && (
        <EmptyDiv />
      )}
    </ImageContainer>
  );
};

export default Image;
